/**
 * Nationwide Dashboard Service - 全国看板数据服务（按模块分接口）
 *
 * 设计参考：dashboardService.ts
 * 区别：
 *   1) 不带 warehouse_code（全国维度）
 *   2) 后端 SQL/方法独立，不与 dashboard 复用
 *   3) 仅按 nationwide-dashboard 实际三个组件拆 3 个接口
 */

import { config } from '../../config';
import * as nationwideDashboardMock from '../../mocks/nationwide-dashboard';
import httpClient from '../httpClient';
import { API_ENDPOINTS } from '../../utils/apiEndpoints';

// ===== 3 个独立模块接口（POST，无入参） =====

export async function getExecutiveSummary() {
  return httpClient.post(API_ENDPOINTS.NATIONWIDE_DASHBOARD.EXECUTIVE_SUMMARY, {});
}

export async function getRealtimeOperations() {
  return httpClient.post(API_ENDPOINTS.NATIONWIDE_DASHBOARD.REALTIME_OPERATIONS, {});
}

export async function getRankings() {
  return httpClient.post(API_ENDPOINTS.NATIONWIDE_DASHBOARD.RANKINGS, {});
}

// ===== 聚合方法：并行调用 3 个模块接口，组装成前端组件需要的数据结构 =====

export async function getNationwideDashboardData() {
  if (config.mock) {
    return nationwideDashboardMock.mockGetNationwideDashboardData();
  }

  const results = await Promise.allSettled([
    getExecutiveSummary(),
    getRealtimeOperations(),
    getRankings(),
  ]);

  const extractData = (result: PromiseSettledResult<any>, label: string) => {
    if (result.status === 'fulfilled') {
      return result.value?.data ?? {};
    }
    console.warn(`[NationwideDashboard] ${label} failed:`, result.reason);
    return {};
  };

  const summary = extractData(results[0], 'executiveSummary');
  const realtime = extractData(results[1], 'realtimeOperations');
  const rankings = extractData(results[2], 'rankings');

  const onlinePickers = realtime.onlinePickers ?? 0;
  const offlinePickers = realtime.offlinePickers ?? 0;

  return {
    success: true,
    data: {
      // ExecutiveSummary 组件所需字段
      executiveSummary: {
        todayTotalOrders: summary.todayTotalOrders ?? 0,
        orderShortageRate: summary.orderShortageRate ?? 0,
        avgPickingAndSortingTime: summary.avgPickingAndSortingTimePerOrder ?? 0,
        // RealtimeOperations 组件需要透传的字段
        pickedOrders: realtime.pickedOrders ?? 0,
        onlinePickers,
        totalPickers: onlinePickers + offlinePickers,
      },

      // RealtimeOperations 组件所需字段
      realtimeOperations: {
        funnel: {
          waitingOrders: realtime.waitingOrders ?? 0,
          pickingOrders: realtime.pickingAndPackingOrders ?? 0,
          pickedOrders: realtime.pickedOrders ?? 0,
          totalIncompleteOrders: realtime.totalIncompleteOrders ?? 0,
        },
        cancelOrders: realtime.cancelOrders ?? 0,
        timeoutOrders: realtime.timeoutOrders ?? 0,
      },

      // RankingsPanel 组件所需字段
      rankings: {
        storeDurationRanking: (rankings.storeDurationRanking ?? []).map((store: any) => {
          // 主数据 clusterSection 映射到前端展示 label（与表格 typeConfig 保持一致）
          const typeMap: Record<string, string> = {
            GF: 'Go Fresh',
            SUPER: 'Super',
            HYPER: 'Hyper',
          };
          const rawType: string = store.storeType ?? '';
          // 后端返回 avgDurationSeconds 为秒，前端表格展示单位为分钟，转换后保留 1 位小数
          const avgSeconds: number = store.avgDurationSeconds ?? 0;
          const avgMinutes = Math.round((avgSeconds / 60) * 10) / 10;
          return {
            storeName: store.storeName || store.warehouseCode,
            storeCode: store.warehouseCode,
            storeType: typeMap[rawType] ?? rawType,
            avgDuration: avgMinutes,
          };
        }),
        shortageItemRanking: (rankings.shortageItemsRanking ?? []).map((item: any) => {
          const codes: string[] = item.warehouseCodes ?? [];
          return {
            sku: item.itemCode,
            itemName: item.itemName,
            shortageCount: item.count,
            affectedStoreCodes: codes,
            affectedStores: codes.length,
          };
        }),
      },
    },
  };
}
